import { useRef, useState } from 'react';
import { Message } from '@shared/types';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Copy,
  Pencil,
  X,
  Wrench,
  User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useConversation } from '@/services/conversationService';
import { useCurrentMessage } from '@/contexts/CurrentMessageContext';
import { ImageViewer } from '@/components/ImageViewer';
import { TreeNode } from '@shared/Tree';
import { useEditMessageMutation } from '@/services/messageService';

interface UserMessageProps {
  isLoading: boolean;
  message: TreeNode<Message>;
}

export function UserMessage({ message, isLoading }: UserMessageProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [copied, setCopied] = useState(false);
  const [input, setInput] = useState(message.content.text);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { conversation, updateConversation } = useConversation();
  const { mutate: editMessage } = useEditMessageMutation();

  const changeLeaf = (messageId: string) => {
    updateConversation({
      ...conversation,
      current_message_leaf_id: messageId,
    });
  };

  const branchIndex = message.siblings.findIndex(
    (branch) => branch.id === message.id,
  );

  const leafNodes = message.siblings.map((branch) => {
    let current = branch;
    while (current.children && current.children.length > 0) {
      current = current.children[0];
    }
    return current;
  });

  const handleEdit = () => {
    editMessage({
      ...message,
      content: {
        ...message.content,
        text: input,
      },
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setInput(message.content.text);
    setIsEditing(false);
  };

  const handleMouseEnter = () => {
    setHovering(true);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleMouseLeave = () => {
    setHovering(false);
    setCopied(false);
  };

  const handleCopy = () => {
    if (message.content.text) {
      navigator.clipboard.writeText(message.content.text);
      setCopied(true);
    }
  };

  return (
    <div className="group flex gap-2">
      {/* Avatar */}
      <div className="flex-shrink-0">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-dc-accent-secondary/20 to-dc-accent-tertiary/20 ring-1 ring-dc-accent-secondary/30">
          <User className="h-4 w-4 text-dc-accent-secondary" />
        </div>
      </div>

      {/* Message Content */}
      <div
        className="min-w-0 flex-1"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Header */}
        <div className="mb-1 flex items-center gap-2">
          <span className="text-xs font-semibold text-dc-text-primary">You</span>
        </div>

        {/* Error state */}
        {message.content.error ? (
          <div className="rounded-2xl rounded-tl-md border border-red-500/30 bg-red-500/10 p-4">
            <div className="group/fix flex cursor-pointer items-center gap-2 text-sm text-red-400 transition-colors hover:text-red-300">
              <Wrench className="h-4 w-4 transition-transform group-hover/fix:rotate-12" />
              <span>Fix with AI</span>
            </div>
          </div>
        ) : (
          <>
            {/* Images */}
            {message.content.images && message.content.images.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                <UserMessageImagesViewer message={message} />
              </div>
            )}

            {/* Text Message */}
            {(isEditing || (input && input.length > 0)) && (
              <div
                className={cn(
                  'rounded-xl rounded-tl-sm border transition-all duration-200',
                  isEditing
                    ? 'border-dc-accent-primary/50 bg-dc-bg-tertiary/80'
                    : 'border-dc-border-primary bg-dc-bg-tertiary/50 hover:border-dc-border-hover',
                )}
              >
                {isEditing ? (
                  <Textarea
                    value={input}
                    ref={textareaRef}
                    onChange={(e) => setInput(e.target.value)}
                    className="block h-auto min-h-0 w-full resize-none overflow-hidden whitespace-pre-line break-all rounded-xl border-none bg-transparent px-3 py-2 text-xs text-dc-text-primary placeholder:text-dc-text-muted focus:ring-0"
                    rows={1}
                  />
                ) : (
                  <p className="break-words px-3 py-2 text-xs leading-relaxed text-dc-text-primary">
                    {input}
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {/* Action Bar */}
        {((hovering && (message.content.text || message.siblings.length > 1)) || isEditing) && (
          <div className="mt-2 flex items-center gap-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
            {!isEditing ? (
              <>
                {/* Edit button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        'h-7 gap-1.5 rounded-lg px-2 text-xs text-dc-text-tertiary',
                        isLoading
                          ? 'cursor-not-allowed opacity-50'
                          : 'hover:bg-dc-bg-hover hover:text-dc-text-primary',
                      )}
                      onClick={() => setIsEditing(true)}
                      disabled={isLoading}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-dc-bg-elevated border-dc-border-primary">
                    <span className="text-dc-text-primary">Edit message</span>
                  </TooltipContent>
                </Tooltip>

                {/* Copy button */}
                {message.content.text && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1.5 rounded-lg px-2 text-xs text-dc-text-tertiary hover:bg-dc-bg-hover hover:text-dc-text-primary"
                        onClick={handleCopy}
                      >
                        {copied ? (
                          <>
                            <Check className="h-3.5 w-3.5 text-green-400" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-3.5 w-3.5" />
                            Copy
                          </>
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="bg-dc-bg-elevated border-dc-border-primary">
                      <span className="text-dc-text-primary">Copy to clipboard</span>
                    </TooltipContent>
                  </Tooltip>
                )}

                {/* Branch Navigation */}
                {message.siblings.length > 1 && (
                  <div className="flex items-center gap-1 rounded-lg bg-dc-bg-tertiary px-1 ring-1 ring-dc-border-primary">
                    <Button
                      disabled={branchIndex === 0 || isLoading}
                      variant="ghost"
                      size="icon"
                      onClick={() => changeLeaf(leafNodes[branchIndex - 1].id)}
                      className="h-6 w-6 rounded-md p-0 text-dc-text-tertiary hover:bg-dc-bg-hover hover:text-dc-text-primary disabled:opacity-30"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="min-w-[2rem] text-center text-xs font-medium text-dc-text-secondary">
                      {branchIndex + 1}/{message.siblings.length}
                    </span>
                    <Button
                      disabled={branchIndex === message.siblings.length - 1 || isLoading}
                      variant="ghost"
                      size="icon"
                      onClick={() => changeLeaf(leafNodes[branchIndex + 1].id)}
                      className="h-6 w-6 rounded-md p-0 text-dc-text-tertiary hover:bg-dc-bg-hover hover:text-dc-text-primary disabled:opacity-30"
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Save/Cancel when editing */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEdit}
                  className="h-7 gap-1.5 rounded-lg bg-dc-accent-primary/10 px-2 text-xs text-dc-accent-primary hover:bg-dc-accent-primary/20"
                >
                  <Check className="h-3.5 w-3.5" />
                  Save
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1.5 rounded-lg px-2 text-xs text-dc-text-tertiary hover:bg-dc-bg-hover hover:text-dc-text-primary"
                  onClick={handleCancel}
                >
                  <X className="h-3.5 w-3.5" />
                  Cancel
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function UserMessageImagesViewer({ message }: { message: Message }) {
  const { currentMessage, setCurrentMessage } = useCurrentMessage();

  if (!message.content.images) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {message.content.images.map((image: string, index: number) => (
        <div
          key={`${image}-${index}`}
          onClick={() => {
            if (
              currentMessage &&
              message.id === currentMessage?.id &&
              currentMessage?.content.index === index
            ) {
              setCurrentMessage(null);
            } else {
              setCurrentMessage({
                ...message,
                content: {
                  images: message.content.images,
                  index,
                },
              });
            }
          }}
          className="h-20 w-20 overflow-hidden rounded-xl ring-1 ring-dc-border-primary transition-all duration-200 hover:ring-dc-accent-primary/50"
        >
          <ImageViewer
            image={image}
            className={cn(
              'aspect-square cursor-pointer transition-all duration-200 hover:scale-105',
              currentMessage?.id === message.id &&
              currentMessage?.content.index === index &&
              'ring-2 ring-dc-accent-primary',
            )}
          />
        </div>
      ))}
    </div>
  );
}
